import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid, Legend,
} from "recharts";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { TrendingUp, MapPin, Building2, Tag, Calendar, Loader2 } from "lucide-react";

/**
 * EstadisticasView — Vista de estadísticas reutilizable (admin y portal cliente).
 * Props:
 *  - mode: "admin" | "portal"
 *  - clientesList?: [{id, nombre}] (solo admin)
 */
export function EstadisticasView({ mode, clientesList, apiClient, onEspacioClick }) {
  const fetcher = apiClient || api;
  const [filtros, setFiltros] = useState({ cliente_id: "", desde: "", hasta: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (mode === "admin" && filtros.cliente_id) params.cliente_id = filtros.cliente_id;
      if (filtros.desde) params.desde = filtros.desde;
      if (filtros.hasta) params.hasta = filtros.hasta;
      const endpoint = mode === "admin" ? "/estadisticas" : "/portal/estadisticas";
      const { data: d } = await fetcher.get(endpoint, { params });
      setData(d);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [filtros.cliente_id, filtros.desde, filtros.hasta]); // eslint-disable-line

  const heatmapCenter = useMemo(() => {
    if (!data || !data.heatmap?.length) return [40.4, -3.7]; // Madrid default
    const lats = data.heatmap.map((p) => p.lat);
    const lngs = data.heatmap.map((p) => p.lng);
    return [(Math.min(...lats) + Math.max(...lats)) / 2, (Math.min(...lngs) + Math.max(...lngs)) / 2];
  }, [data]);

  return (
    <div>
      {/* Filtros */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-3 mb-4 flex flex-wrap items-end gap-3">
        {mode === "admin" && clientesList && (
          <div className="min-w-[200px]">
            <Label className="text-xs text-zinc-500">Cliente</Label>
            <Select value={filtros.cliente_id || "_all"} onValueChange={(v) => setFiltros({ ...filtros, cliente_id: v === "_all" ? "" : v })}>
              <SelectTrigger data-testid="stats-cliente-filter" className="bg-zinc-900 border-zinc-800 h-9"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                <SelectItem value="_all">Todos los clientes</SelectItem>
                {clientesList.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label className="text-xs text-zinc-500">Desde</Label>
          <Input type="date" value={filtros.desde} onChange={(e) => setFiltros({ ...filtros, desde: e.target.value })}
            data-testid="stats-desde" className="bg-zinc-900 border-zinc-800 h-9 text-sm" />
        </div>
        <div>
          <Label className="text-xs text-zinc-500">Hasta</Label>
          <Input type="date" value={filtros.hasta} onChange={(e) => setFiltros({ ...filtros, hasta: e.target.value })}
            data-testid="stats-hasta" className="bg-zinc-900 border-zinc-800 h-9 text-sm" />
        </div>
        {(filtros.desde || filtros.hasta || filtros.cliente_id) && (
          <Button size="sm" variant="outline" onClick={() => setFiltros({ cliente_id: "", desde: "", hasta: "" })}
            className="bg-transparent border-zinc-700 text-zinc-300 h-9">Limpiar</Button>
        )}
      </div>

      {loading && <div className="text-center py-12 text-zinc-500"><Loader2 className="w-6 h-6 animate-spin inline" /></div>}

      {!loading && data && (
        <div className="space-y-4">
          {/* KPI row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Peticiones" value={data.total_peticiones} icon={Tag} />
            <KpiCard label="Trabajos (espacios)" value={data.total_lineas || 0} icon={MapPin} />
            <KpiCard label="Cadenas distintas" value={data.por_cadena.length} icon={Building2} />
            <KpiCard label="Provincias" value={data.por_provincia.length} icon={MapPin} />
          </div>

          {data.total_peticiones === 0 && (
            <div className="text-center py-12 text-zinc-500">Sin datos para los filtros seleccionados.</div>
          )}

          {data.total_peticiones > 0 && (
            <>
              {/* Serie mensual */}
              <ChartCard title="Trabajos por mes" icon={Calendar}>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={data.serie_mensual}>
                    <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
                    <XAxis dataKey="mes" tick={{ fontSize: 11, fill: "#a1a1aa" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#a1a1aa" }} />
                    <Tooltip contentStyle={{ background: "#0a0a0c", border: "1px solid #27272a", fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="trabajos" stroke="#f59e0b" strokeWidth={2} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>

              {/* Grid de dos: cadenas + provincias */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ChartCard title="Top cadenas" icon={Building2}>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={data.por_cadena.slice(0, 10)} layout="vertical" margin={{ left: 60 }}>
                      <CartesianGrid stroke="#27272a" strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11, fill: "#a1a1aa" }} />
                      <YAxis dataKey="cadena" type="category" tick={{ fontSize: 11, fill: "#a1a1aa" }} width={80} />
                      <Tooltip contentStyle={{ background: "#0a0a0c", border: "1px solid #27272a", fontSize: 12 }} />
                      <Bar dataKey="trabajos" fill="#a78bfa" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>

                <ChartCard title="Por provincia" icon={MapPin}>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={data.por_provincia.slice(0, 10)} layout="vertical" margin={{ left: 80 }}>
                      <CartesianGrid stroke="#27272a" strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11, fill: "#a1a1aa" }} />
                      <YAxis dataKey="provincia" type="category" tick={{ fontSize: 11, fill: "#a1a1aa" }} width={110} />
                      <Tooltip contentStyle={{ background: "#0a0a0c", border: "1px solid #27272a", fontSize: 12 }} />
                      <Bar dataKey="trabajos" fill="#10b981" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </div>

              {/* Top espacios tabla */}
              <ChartCard title="Top espacios" icon={MapPin}>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-zinc-500 border-b border-zinc-800">
                        <th className="text-left py-1.5">#</th>
                        <th className="text-left">Espacio</th>
                        <th className="text-left">Cadena</th>
                        <th className="text-left">Provincia</th>
                        <th className="text-right">Trabajos</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.por_espacio.slice(0, 20).map((e, i) => (
                        <tr key={e.nombre}
                          onClick={onEspacioClick && e.espacio_id ? () => onEspacioClick(e) : undefined}
                          data-testid={`stats-espacio-row-${i}`}
                          className={`border-b border-zinc-900 hover:bg-zinc-900/40 ${onEspacioClick && e.espacio_id ? "cursor-pointer" : ""}`}>
                          <td className="py-1.5 text-zinc-500">{i + 1}</td>
                          <td className="font-medium text-zinc-200">
                            {e.nombre}
                            {onEspacioClick && e.espacio_id && (
                              <span className="ml-1.5 text-[10px] text-amber-400/70">📷 ver galería</span>
                            )}
                          </td>
                          <td className="text-violet-300">{e.cadena}</td>
                          <td className="text-zinc-400">{e.provincia}</td>
                          <td className="text-right text-amber-300 font-mono">{e.trabajos}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </ChartCard>

              {/* Mapa de calor */}
              {data.heatmap.length > 0 && (
                <ChartCard title={`Mapa de actividad (${data.heatmap.length} ubicaciones)`} icon={MapPin}>
                  <div className="h-[420px] rounded overflow-hidden border border-zinc-800">
                    <MapContainer center={heatmapCenter} zoom={6} style={{ height: "100%", width: "100%" }}>
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; OpenStreetMap'
                      />
                      {data.heatmap.map((p, i) => (
                        <CircleMarker
                          key={i}
                          center={[p.lat, p.lng]}
                          radius={6 + Math.min(p.weight * 2, 18)}
                          fillColor="#f59e0b"
                          color="#f59e0b"
                          weight={1.5}
                          fillOpacity={0.55}
                        >
                          <Popup><strong>{p.nombre}</strong></Popup>
                        </CircleMarker>
                      ))}
                    </MapContainer>
                  </div>
                </ChartCard>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, icon: Icon }) {
  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3 flex items-center gap-3">
      <Icon className="w-5 h-5 text-amber-400" />
      <div>
        <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
        <div className="text-xl font-bold text-zinc-100">{value}</div>
      </div>
    </div>
  );
}

function ChartCard({ title, icon: Icon, children }) {
  return (
    <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-3">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-zinc-500 mb-2">
        {Icon && <Icon className="w-3 h-3" />} {title}
      </div>
      {children}
    </div>
  );
}

export default function Estadisticas() {
  const [clientes, setClientes] = useState([]);
  useEffect(() => {
    api.get("/clientes").then(({ data }) => setClientes(data));
  }, []);
  return (
    <div className="p-8 max-w-[1800px]">
      <PageHeader title="Estadísticas" subtitle="Análisis global y por cliente · cadenas · provincias · evolución temporal" />
      <EstadisticasView mode="admin" clientesList={clientes} />
    </div>
  );
}
